// Events and tours, virtual-race style: an event is a tour with a single stage.
// A definition describes the categories (ranges of Gruvetto score), the stages (each with its game mode), the jerseys
// and the scoring. The engine derives deterministic race options from it: same bots, same strengths at every stage.
// Everything goes through normalizeEvent: what an AI or a player writes is bounded here, never run as-is.
import { ITEM_IDS, type ItemId } from './items.js';
import { TRACKS, buildTrack } from './track.js';
import { climbsOf, KOM_LABEL, segmentsOf, type SegmentDef } from './segments.js';
export { climbsOf, KOM_LABEL, type Climb } from './segments.js';
import { MODES } from './modes.js';
import { clamp } from './physics.js';
import { mulberry, BOT_NAMES, BOT_COLORS, MAX_BOTS, MAX_RIDERS, type BotSpec, type BotSpecialty, type CheckpointDef, type HumanSpec, type Look, type RaceOptions } from './race.js';
import { powerScore, wkgForScore, RATING } from './rating.js';

// ---------- Categories ----------
/** Stored identifiers of the categories (event definitions, sign-ups): they stay letters, players see score ranges. */
export type CatId = 'A' | 'B' | 'C' | 'D' | 'E';
/** A category is a range of Gruvetto score (see rating.ts); `E` is open to everyone. */
export interface CatDef { id: CatId; name: string; color: string; minScore: number; maxScore: number; desc: string }
// Boundaries kept from the old W/kg letters at the reference mass (2.5, 3.2 and 4.0 W/kg): 300, 440 and 600 points.
export const EVENT_CATS: Record<CatId, CatDef> = {
  A: { id: 'A', name: '600+', color: '#ff3b3b', minScore: 600, maxScore: RATING.max + 1, desc: '600 points and above' },
  B: { id: 'B', name: '440–599', color: '#3ddc84', minScore: 440, maxScore: 600, desc: '440 to 599 points' },
  C: { id: 'C', name: '300–439', color: '#4aa3ff', minScore: 300, maxScore: 440, desc: '300 to 439 points' },
  D: { id: 'D', name: '0–299', color: '#ffd400', minScore: 0, maxScore: 300, desc: 'under 300 points' },
  E: { id: 'E', name: 'Open', color: '#c4b5fd', minScore: 0, maxScore: RATING.max + 1, desc: 'every score' },
};
export const CAT_IDS = Object.keys(EVENT_CATS) as CatId[];

/** A rider's natural category from their Gruvetto score. */
export function catForScore(score: number): CatId {
  return (['A', 'B', 'C', 'D'] as const).find(c => score >= EVENT_CATS[c].minScore) ?? 'D';
}

/** Categories open to a rider. With category control, you can ride higher, never lower. */
export function eligibleCats(def: Pick<EventDef, 'categories' | 'enforce'>, score: number): CatId[] {
  if (!def.enforce) return [...def.categories];
  return def.categories.filter(c => c === 'E' || score < EVENT_CATS[c].maxScore);
}
export function suggestedCat(def: Pick<EventDef, 'categories' | 'enforce'>, score: number): CatId | null {
  const ok = eligibleCats(def, score), own = catForScore(score);
  return ok.includes(own) ? own : ok.find(c => c !== 'E') ? ok.filter(c => c !== 'E').sort((a, b) => EVENT_CATS[a].minScore - EVENT_CATS[b].minScore)[0] : ok[0] ?? null;
}

// ---------- Definition ----------
/** Modes a stage can use; `ttt` (team time trial) ranks teams, see scoring.ts. */
export type StageMode = 'race' | 'tt' | 'ttt' | 'elim' | 'free';
export const STAGE_MODES: StageMode[] = ['race', 'tt', 'ttt', 'elim', 'free'];
/** Modes ridden against the clock: the stage is scored by time, with no bonus seconds. */
export const CLOCK_MODES: StageMode[] = ['tt', 'ttt'];
export type StageProfile = 'plat' | 'vallonne' | 'montagne' | 'clm';
export const PROFILES: Record<StageProfile, { name: string; icon: string }> = {
  plat: { name: 'Flat', icon: '➖' }, vallonne: { name: 'Rolling', icon: '〰️' }, montagne: { name: 'Mountain', icon: '⛰️' }, clm: { name: 'Time trial', icon: '⏱️' },
};

export interface StageDef {
  name: string; desc?: string; mode: StageMode; track: string; laps: number;
  /** Scheduled start of a later stage of a dated tour (ms). The first stage starts at the event's startAt. */
  startAt?: number;
  groupWkg?: number; profile?: StageProfile;
  /** Intermediate sprints, as a fraction of the stage distance (0 to 1). */
  sprints: number[];
  /** King of the Mountains points at the top of each climb. */
  koms: boolean;
  items?: boolean;
  /** Items the organiser kept for this stage; absent: all of them, including those added to the game later. */
  itemPool?: ItemId[];
  /** Segments of the track contested for jersey points (ADR 0031); absent: none. */
  segments?: StageSegmentRule[];
}

/** How a stage contests a segment: by the fastest time through it, or by the order past its end; for which table. */
export interface StageSegmentRule { id: string; by: 'first' | 'fastest'; objective: 'sprint' | 'mountain' }
export const SEGMENT_BY = ['first', 'fastest'] as const;
export const SEGMENT_OBJECTIVES = ['sprint', 'mountain'] as const;
/** Segments a stage may contest: those of its track, when the stage is a race or a time trial. */
export function stageSegments(st: Pick<StageDef, 'mode' | 'track'>): SegmentDef[] {
  return st.mode === 'race' || st.mode === 'tt' ? segmentsOf(buildTrack(st.track)) : [];
}

export type JerseyObjective = 'gc' | 'points' | 'mountain' | 'sprint' | 'rookie' | 'combative' | 'wins';
export interface JerseyDef { id: string; name: string; color: string; pattern: string; objective: JerseyObjective; desc?: string }
export const JERSEYS: Record<JerseyObjective, Omit<JerseyDef, 'id' | 'objective'>> = {
  gc: { name: 'Yellow jersey', color: '#ffd400', pattern: 'jaune', desc: 'General classification leader' },
  points: { name: 'Green jersey', color: '#1fa34a', pattern: 'uni', desc: 'Points classification: finishes and intermediate sprints' },
  mountain: { name: 'Polka-dot jersey', color: '#d7141a', pattern: 'pois', desc: 'Best climber: points at the top of climbs' },
  rookie: { name: 'White jersey', color: '#f4f4f4', pattern: 'uni', desc: 'Best rookie in the general (level 10 or below)' },
  sprint: { name: 'Red jersey', color: '#e0262f', pattern: 'uni', desc: 'King of the intermediate sprints' },
  combative: { name: 'Combativity jersey', color: '#ff7a1a', pattern: 'bandes', desc: 'Most hits with items' },
  wins: { name: 'Rainbow jersey', color: '#fafafa', pattern: 'arcenciel', desc: 'Most stage wins' },
};
export const JERSEY_OBJECTIVES = Object.keys(JERSEYS) as JerseyObjective[];
export const jersey = (objective: JerseyObjective, over: Partial<JerseyDef> = {}): JerseyDef => ({ id: objective, objective, ...JERSEYS[objective], ...over });

export interface EventDef {
  v: 1; id: string; kind: 'event' | 'tour'; name: string; desc: string; emoji: string;
  /** Scheduled start (ms); null = on demand. */
  startAt: number | null;
  /** Riders may join a stage in progress: they start from the line with their own clock (race, time trial and group ride stages). */
  lateJoin: boolean;
  categories: CatId[]; enforce: boolean; minLevel: number;
  /** Extra or fewer laps per category, like different distances per category. */
  catLaps: Partial<Record<CatId, number>>;
  /** Number of bots per category. */
  bots: number;
  stages: StageDef[]; jerseys: JerseyDef[];
  scoring: { gc: 'time' | 'points'; bonuses: boolean };
  seed: number; tags: string[];
  author?: string | null; prompt?: string | null;
}

export const LIMITS = { stages: 21, laps: 10, bots: MAX_BOTS, jerseys: 6, sprints: 4, segments: 6, name: 60, desc: 500 };

const hash = (s: string): number => { let h = 2166136261; for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619); return h >>> 0; };
const str = (v: unknown, max: number, d = ''): string => (typeof v === 'string' ? v.replace(/[<>]/g, '').trim().slice(0, max) : d);
const numOr = (v: unknown, d: number): number => (typeof v === 'number' && Number.isFinite(v) ? v : typeof v === 'string' && v.trim() && Number.isFinite(+v) ? +v : d);

/** Distance of a stage (m) for the base category, or null for a mode with no fixed distance. */
export function stageDistance(st: Pick<StageDef, 'mode' | 'track' | 'laps'>, extraLaps = 0): number | null {
  if (st.mode === 'elim') return null;
  const tr = buildTrack(st.track);
  return tr.lap * (tr.open ? 1 : Math.max(1, st.laps + extraLaps));
}

/** Profile of a stage: explicit, otherwise inferred from the mode and the elevation per lap. */
export function stageProfile(st: Pick<StageDef, 'mode' | 'track' | 'profile'>): StageProfile {
  if (st.profile) return st.profile;
  if (CLOCK_MODES.includes(st.mode)) return 'clm';
  const climb = buildTrack(st.track).climb;
  return climb >= 60 ? 'montagne' : climb >= 20 ? 'vallonne' : 'plat';
}

// ---------- Checkpoints ----------
/** Intermediate sprints and summits of a stage, as distance from the start (at most 8 summits, spread out). */
export function stageCheckpoints(st: StageDef): CheckpointDef[] {
  if (st.mode !== 'race' && st.mode !== 'tt') return [];
  const tr = buildTrack(st.track), D = tr.lap * st.laps, out: CheckpointDef[] = [];
  st.sprints.forEach((f, i) => out.push({ id: 's' + i, kind: 'sprint', s: Math.round(clamp(f, 0.05, 0.95) * D), label: 'Intermediate sprint' }));
  if (st.koms) {
    let tops: CheckpointDef[] = [];
    for (let lap = 0; lap < st.laps; lap++) for (const c of climbsOf(tr)) {
      const s = lap * tr.lap + c.top;
      if (s > 80 && s < D - 20) tops.push({ id: `k${lap}-${Math.round(c.top)}`, kind: 'kom', s: Math.round(s), cat: c.cat, label: `Summit, ${KOM_LABEL(c.cat)}` });
    }
    if (tops.length > 8) { const step = tops.length / 8; tops = Array.from({ length: 8 }, (_, i) => tops[Math.min(tops.length - 1, Math.round((i + 1) * step) - 1)]); }
    out.push(...tops);
  }
  return out.sort((a, b) => a.s - b.s);
}

// ---------- Scheduled starts ----------
/** A dated start opens its room this long before the gun: the entrants gather and wait at the line. */
export const START_LOBBY_MS = 15 * 60_000;
/** Without a time of its own, a stage of a dated tour starts this long after the one before. */
export const STAGE_GAP_MS = 24 * 3600_000;
/** An on-demand start needs this many riders at the line: an event is ridden with others, Race is there to ride alone. */
export const EVENT_MIN_RIDERS = 2;
type Schedule = Pick<EventDef, 'startAt'> & { stages?: Pick<StageDef, 'startAt'>[] };
/** Start time of a stage: every stage of a dated event is scheduled; an on-demand event has none. Definitions stored before stage times fall back on one stage a day. */
export const stageStartAt = (def: Schedule, stage: number): number | null =>
  def.startAt == null ? null : stage === 0 ? def.startAt : (def.stages?.[stage]?.startAt ?? def.startAt + stage * STAGE_GAP_MS);
/** The first scheduled start after a time, or null when none is left (or the event is on demand). */
export function nextStartAt(def: Schedule & { stages: unknown[] }, after: number): number | null {
  for (let i = 0; i < def.stages.length; i++) { const at = stageStartAt(def, i); if (at != null && at > after) return at; }
  return null;
}
export type StartWindow =
  | { kind: 'free' } // on demand: whoever hosts the room starts the race
  | { kind: 'closed'; opensAt: number; startAt: number } // too early: the line opens START_LOBBY_MS before the gun
  | { kind: 'open'; startAt: number } // gather at the line; the gun fires at startAt, nobody can start it earlier
  | { kind: 'gone'; startAt: number }; // the gun has been given: this start can no longer be joined
/** Whether a stage can be joined online right now, the same rule for the client and the game server. */
/** Modes where a rider can start from the line while others race: not an elimination. */
const LATE_MODES: StageMode[] = ['race', 'tt', 'free'];
/** Whether the organizer lets riders join this stage in progress, from the line with their own clock. */
export const stageLateJoin = (def: Pick<EventDef, 'lateJoin' | 'stages'>, stage: number): boolean => !!def.lateJoin && LATE_MODES.includes(def.stages[stage]?.mode);

export function startWindow(def: Schedule, stage: number, now: number): StartWindow {
  const startAt = stageStartAt(def, stage);
  if (startAt == null) return { kind: 'free' };
  if (now >= startAt) return { kind: 'gone', startAt };
  if (now < startAt - START_LOBBY_MS) return { kind: 'closed', opensAt: startAt - START_LOBBY_MS, startAt };
  return { kind: 'open', startAt };
}

// ---------- Normalisation (the engine decides) ----------
export interface Normalized { def: EventDef; warnings: string[] }

/** Turns any input (form, AI, file) into a valid, bounded definition, with the list of corrections. */
export function normalizeEvent(raw: unknown, opts: { now?: number } = {}): Normalized {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, any>, w: string[] = [];
  const name = str(r.name, LIMITS.name) || 'Unnamed event';
  const stagesIn: any[] = Array.isArray(r.stages) && r.stages.length ? r.stages : [{}];
  if (stagesIn.length > LIMITS.stages) w.push(`${stagesIn.length} stages requested: limited to ${LIMITS.stages}.`);
  const stages = stagesIn.slice(0, LIMITS.stages).map((s, i) => normalizeStage(s, i, w));
  let cats = (Array.isArray(r.categories) ? r.categories : []).map((c: unknown) => String(c).toUpperCase()).filter((c: string): c is CatId => c in EVENT_CATS);
  cats = [...new Set(cats)] as CatId[];
  if (!cats.length) cats = ['A', 'B', 'C', 'D'];
  cats.sort((a, b) => CAT_IDS.indexOf(a) - CAT_IDS.indexOf(b));
  const catLaps: Partial<Record<CatId, number>> = {};
  for (const [k, v] of Object.entries(r.catLaps && typeof r.catLaps === 'object' ? r.catLaps : {})) {
    const c = k.toUpperCase() as CatId, n = Math.round(numOr(v, 0));
    if (cats.includes(c) && n) catLaps[c] = clamp(n, -3, 3);
  }
  const kind: EventDef['kind'] = stages.length > 1 ? 'tour' : r.kind === 'tour' ? 'tour' : 'event';
  const jerseys: JerseyDef[] = [];
  for (const j of Array.isArray(r.jerseys) ? r.jerseys : []) {
    const obj = (typeof j === 'string' ? j : j?.objective) as JerseyObjective;
    if (!JERSEYS[obj]) { w.push(`Jersey ignored: unknown objective “${String(obj)}”.`); continue; }
    if (jerseys.some(x => x.objective === obj)) continue;
    if (jerseys.length >= LIMITS.jerseys) { w.push(`At most ${LIMITS.jerseys} jerseys.`); break; }
    const color = typeof j?.color === 'string' && /^#[0-9a-f]{6}$/i.test(j.color) ? j.color : JERSEYS[obj].color;
    const pattern = typeof j?.pattern === 'string' && ['uni', 'bandes', 'degrade', 'pois', 'damier', 'arcenciel', 'jaune'].includes(j.pattern) ? j.pattern : JERSEYS[obj].pattern;
    jerseys.push(jersey(obj, { name: str(j?.name, 40) || JERSEYS[obj].name, desc: str(j?.desc, 140) || JERSEYS[obj].desc, color, pattern }));
  }
  if (kind === 'tour' && !jerseys.some(j => j.objective === 'gc')) jerseys.unshift(jersey('gc'));
  let startAt = readTime(r.startAt);
  if (startAt != null && !Number.isFinite(startAt)) { w.push('Unreadable start date: event set to on demand.'); startAt = null; }
  const now = opts.now ?? Date.now();
  if (startAt != null && startAt > now + 366 * 86400000) { w.push('Start more than a year away: set back to on demand.'); startAt = null; }
  scheduleStages(stages, startAt, now, w);
  const bots = Math.round(clamp(numOr(r.bots, 7), 0, LIMITS.bots));
  const def: EventDef = {
    v: 1, id: str(r.id, 60).replace(/[^\w-]/g, '') || 'ev-' + hash(name + now).toString(36),
    kind, name, desc: str(r.desc, LIMITS.desc), emoji: str(r.emoji, 8) || (kind === 'tour' ? '🏁' : '🚴'),
    startAt: startAt == null ? null : Math.round(startAt),
    lateJoin: r.lateJoin === true,
    categories: cats, enforce: r.enforce !== false && cats.some(c => c !== 'E'), minLevel: Math.round(clamp(numOr(r.minLevel, 1), 1, 50)),
    catLaps, bots, stages, jerseys,
    scoring: { gc: r.scoring?.gc === 'points' ? 'points' : 'time', bonuses: r.scoring?.bonuses !== false },
    seed: (numOr(r.seed, hash(name)) >>> 0) || 1, tags: (Array.isArray(r.tags) ? r.tags : []).map((t: unknown) => str(t, 24)).filter(Boolean).slice(0, 8),
    author: str(r.author, 40) || null, prompt: str(r.prompt, 2000) || null,
  };
  // a stage with no distance left to ride for a category
  for (const [c, n] of Object.entries(catLaps)) if (stages.some(s => (s.mode === 'race' || CLOCK_MODES.includes(s.mode)) && s.laps + (n || 0) < 1)) w.push(`Category ${c}: at least one lap per stage is kept.`);
  return { def, warnings: w };
}

const readTime = (v: unknown): number | null => (v == null || v === '' ? null : typeof v === 'string' && isNaN(+v) ? Date.parse(v) : numOr(v, NaN));

/** Gives every later stage of a dated tour a time after the stage before; an on-demand event has no stage times. */
function scheduleStages(stages: StageDef[], startAt: number | null, now: number, w: string[]) {
  let prev = startAt == null ? 0 : Math.round(startAt);
  stages.forEach((s, i) => {
    const at = readTime(s.startAt);
    delete s.startAt;
    if (startAt == null || i === 0) return;
    const fits = at != null && Number.isFinite(at) && at > prev && at <= now + 2 * 366 * 86400000;
    if (at != null && !fits) w.push(`Stage ${i + 1}: start time moved to the day after the stage before.`);
    s.startAt = prev = fits ? Math.round(at) : prev + STAGE_GAP_MS;
  });
}

function normalizeStage(s: any, i: number, w: string[]): StageDef {
  const label = `Stage ${i + 1}`;
  let mode = String(s?.mode || 'race') as StageMode;
  if ((mode as string) === 'ghost') mode = 'tt';
  if (!STAGE_MODES.includes(mode)) { w.push(`${label}: unknown mode “${mode}”, replaced by a race.`); mode = 'race'; }
  let track = String(s?.track || 'collines');
  if (!TRACKS[track]) { w.push(`${label}: unknown track “${track}”, replaced by the Hills Circuit.`); track = 'collines'; }
  const open = !!TRACKS[track].open;
  // an open road is one run to the finish (ADR 0029): no elimination or free ride there, and a single "lap"
  if (open && (mode === 'elim' || mode === 'free')) { w.push(`${label}: ${TRACKS[track].name} goes from A to B, the ${mode === 'elim' ? 'elimination' : 'free ride'} is replaced by a race.`); mode = 'race'; }
  const lapsIn = Math.round(numOr(s?.laps, CLOCK_MODES.includes(mode) || open ? 1 : 2));
  const laps = open ? 1 : clamp(lapsIn, 1, LIMITS.laps);
  if (laps !== lapsIn) w.push(open ? `${label}: ${TRACKS[track].name} goes from A to B, ridden once.` : `${label}: ${lapsIn} laps reduced to ${laps}.`);
  const st: StageDef = {
    name: str(s?.name, 60) || `${label} · ${TRACKS[track].name}`, desc: str(s?.desc, 300) || undefined, mode, track, laps,
    sprints: [], koms: s?.koms !== false && (mode === 'race' || mode === 'tt'),
  };
  if (s?.startAt != null) st.startAt = s.startAt; // read and bounded with the whole schedule (scheduleStages)
  if (mode === 'free' && s?.groupWkg != null) st.groupWkg = +clamp(numOr(s.groupWkg, 2.5), 1.5, 5).toFixed(1);
  if (s?.profile && PROFILES[s.profile as StageProfile]) st.profile = s.profile;
  if (!CLOCK_MODES.includes(mode) && mode !== 'free') {
    if (typeof s?.items === 'boolean') st.items = s.items;
    if (st.items !== false && Array.isArray(s?.itemPool)) {
      const pool = ITEM_IDS.filter(k => s.itemPool.includes(k));
      if (pool.length < new Set(s.itemPool).size) w.push(`${label}: unknown items ignored.`);
      // nothing ticked is a stage without items; everything ticked stays open to the items added later
      if (!s.itemPool.length) st.items = false;
      else if (pool.length && pool.length < ITEM_IDS.length) st.itemPool = pool;
    }
  }
  if (mode === 'race' || mode === 'tt') {
    const sp = (Array.isArray(s?.sprints) ? s.sprints : mode === 'race' && laps >= 2 ? [0.5] : []).map((f: unknown) => numOr(f, NaN)).filter((f: number) => Number.isFinite(f));
    st.sprints = [...new Set(sp.map((f: number) => +clamp(f > 1 ? f / 100 : f, 0.05, 0.95).toFixed(2)))].sort().slice(0, LIMITS.sprints) as number[];
    const segs = normalizeSegmentRules(s?.segments, stageSegments(st), label, w);
    if (segs.length) st.segments = segs;
  }
  return st;
}

/** The segment contests of a stage: known segments of its track only, one rule each, bounded in number. */
function normalizeSegmentRules(raw: unknown, known: SegmentDef[], label: string, w: string[]): StageSegmentRule[] {
  if (!Array.isArray(raw)) return [];
  const out: StageSegmentRule[] = [];
  for (const r of raw) {
    const id = typeof r === 'string' ? r : String(r?.id ?? ''), seg = known.find(x => x.id === id);
    if (!seg) { w.push(`${label}: unknown segment “${id.slice(0, 40)}” ignored.`); continue; }
    if (out.some(x => x.id === id)) continue;
    if (out.length >= LIMITS.segments) { w.push(`${label}: at most ${LIMITS.segments} contested segments.`); break; }
    const by = SEGMENT_BY.includes(r?.by) ? (r.by as StageSegmentRule['by']) : 'fastest';
    const objective = SEGMENT_OBJECTIVES.includes(r?.objective) ? (r.objective as StageSegmentRule['objective']) : seg.kind === 'climb' ? 'mountain' : 'sprint';
    out.push({ id, by, objective });
  }
  return out;
}

// ---------- Bot roster ----------
const NAMES = BOT_NAMES, COLORS = BOT_COLORS;
const SPECS: BotSpecialty[] = ['grimpeur', 'sprinteur', 'rouleur', 'puncheur'];
export const SPECIALTIES: Record<BotSpecialty, { icon: string; name: string }> = {
  grimpeur: { icon: '⛰️', name: 'Climber' }, sprinteur: { icon: '⚡', name: 'Sprinter' }, rouleur: { icon: '🚂', name: 'Rouleur' }, puncheur: { icon: '💥', name: 'Puncheur' },
};

/** Roster member: FTP is relative (a factor) to follow the open category, matched to the humans. */
export interface RosterBot extends Omit<BotSpec, 'ftp'> { factor: number; spec: BotSpecialty }

/** Deterministic roster of a category: the same seed gives the same riders at every stage and for every player. */
export function buildRoster(def: Pick<EventDef, 'seed' | 'bots'>, cat: CatId): RosterBot[] {
  const rnd = mulberry((def.seed ^ hash('cat' + cat)) >>> 0), ci = CAT_IDS.indexOf(cat);
  return Array.from({ length: def.bots }, (_, i) => {
    const spec = SPECS[(i + ci) % SPECS.length];
    const [m0, m1] = spec === 'grimpeur' ? [57, 65] : spec === 'sprinteur' ? [73, 83] : spec === 'rouleur' ? [69, 79] : [63, 71];
    const k = (i * 7 + ci * 11) % NAMES.length;
    return {
      key: `bot:${cat}:${i}`, name: NAMES[k], color: COLORS[k % COLORS.length], cat, spec, rookie: i % 3 === 2,
      mass: Math.round(m0 + rnd() * (m1 - m0)), factor: +(0.9 + rnd() * 0.17 + (i === 0 ? 0.04 : 0)).toFixed(3),
      look: { pattern: ['uni', 'bandes', 'degrade'][(i + ci) % 3], frame: 'couleur', helmet: 'couleur' },
    };
  });
}

/** Human player entered in a stage, with their Gruvetto score (otherwise derived from their FTP and mass). */
export interface Entrant extends HumanSpec { cat: CatId; score?: number }
const entrantScore = (h: Entrant) => h.score ?? powerScore(h.ftp / (h.mass || RATING.refMass), h.mass);

/** Who wears which jersey at the start of a stage (rider key → jersey). */
export type JerseyWearers = Map<string, JerseyDef>;

/**
 * Race options for stage `i`: roster per category present, laps per category, rules, checkpoints, jerseys.
 * Shared by the client (solo) and the game server (online): same input, same race.
 */
export function raceOptionsForStage(def: EventDef, i: number, humans: Entrant[], opts: { seed?: number; wearers?: JerseyWearers | null } = {}): RaceOptions {
  const st = def.stages[clamp(i, 0, def.stages.length - 1)];
  const cats = [...new Set(humans.map(h => h.cat || 'E'))] as CatId[];
  if (!cats.length) cats.push(def.categories[0] || 'E');
  // the bots of every category share the seats the entrants leave in a MAX_RIDERS field
  const per = Math.min(def.bots, Math.floor(Math.max(0, MAX_RIDERS - humans.length) / cats.length));
  const lapsFor = (c: CatId) => Math.max(1, st.laps + (def.catLaps[c] || 0));
  const wear = (key: string, look: Look | null | undefined) => wearLook(opts.wearers, key, look);
  const roster: BotSpec[] = [];
  for (const c of cats) {
    // bots matched to the score of the category's riders, mass by mass: an even race wherever you sit in the range
    // (the balance bench: fields sorted by score are twice as open as fixed bands)
    const hs = humans.filter(h => (h.cat || 'E') === c);
    const score = hs.length ? hs.reduce((a, h) => a + entrantScore(h), 0) / hs.length : 360;
    for (const b of buildRoster({ seed: def.seed, bots: per }, c)) {
      const wkg = wkgForScore(score, b.mass) * 0.98;
      const ftp = st.mode === 'free' ? ((st.groupWkg ?? wkg * 0.75) * b.mass) / 0.88 : wkg * b.mass * b.factor;
      roster.push({ key: b.key, name: b.name, color: b.color, mass: b.mass, ftp: Math.round(ftp), cat: c, spec: b.spec, rookie: b.rookie, laps: lapsFor(c), look: wear(b.key, b.look) });
    }
  }
  return {
    mode: st.mode, trackId: st.track, laps: st.laps, groupWkg: st.groupWkg,
    bots: roster.length, roster, rules: st.items === false ? { items: false } : st.itemPool ? { itemPool: st.itemPool } : undefined, checkpoints: stageCheckpoints(st),
    seed: opts.seed ?? ((def.seed * 31 + i * 7919) >>> 0),
    humans: humans.map(h => entrantSpec(def, i, h, opts.wearers)),
  };
}

const wearLook = (wearers: JerseyWearers | null | undefined, key: string, look: Look | null | undefined): Look | null => {
  const j = wearers?.get(key);
  return j ? { ...(look || {}), pattern: j.pattern, jerseyColor: j.color } : look || null;
};

/** An entrant as a rider of stage `i`: the laps of their category, the jersey they wear from the previous stages. */
export function entrantSpec(def: EventDef, i: number, h: Entrant, wearers?: JerseyWearers | null): HumanSpec {
  const st = def.stages[clamp(i, 0, def.stages.length - 1)], key = h.key || h.pid || 'local';
  return { ...h, key, laps: Math.max(1, st.laps + (def.catLaps[h.cat || 'E'] || 0)), look: wearLook(wearers, key, h.look) };
}

// ---------- Library and calendar ----------
const stage = (name: string, mode: StageMode, track: string, laps: number, more: Partial<StageDef> = {}): StageDef =>
  ({ name, mode, track, laps, sprints: mode === 'race' && laps >= 2 ? [0.5] : [], koms: mode === 'race' || mode === 'tt', ...more });
const lib = (d: Partial<EventDef> & Pick<EventDef, 'id' | 'name' | 'stages'>): EventDef => normalizeEvent({ categories: ['A', 'B', 'C', 'D', 'E'], bots: 7, ...d }).def;

/** Events and tours shipped with the game, playable on demand. */
export const EVENT_LIBRARY: EventDef[] = [
  lib({
    id: 'tour-gruvetto', kind: 'tour', name: 'Tour de Gruvetto', emoji: '🟡', author: 'Gruvetto',
    desc: 'Six stages, four jerseys. A criterium for the sprinters, hills, a time trial, Cloud Pass, an elimination and the city finale.',
    stages: [
      stage('Opening criterium', 'race', 'crit', 4, { sprints: [0.25, 0.75], desc: 'Flat and nervous: the sprinters take the green.' }),
      stage('The Hills', 'race', 'collines', 3, { desc: 'A climb every lap, the puncheurs attack.' }),
      stage('Wall time trial', 'tt', 'mur', 2, { desc: 'Alone against the clock, no items and no drafting.' }),
      stage('Queen stage: Cloud Pass', 'race', 'nuages', 3, { desc: 'Three passes over the summit, the polka-dot jersey is decided here.' }),
      stage('Hills elimination', 'elim', 'collines', 1, { desc: 'The last rider of each lap is out. Gaps by rank.' }),
      stage('City finale', 'race', 'crit', 5, { sprints: [0.4, 0.8], desc: 'The last stage’s sprint, on the grand boulevards.' }),
    ],
    jerseys: [jersey('gc'), jersey('points'), jersey('mountain'), jersey('rookie')], catLaps: { A: 1, D: -1 }, tags: ['tour', 'official'],
  }),
  lib({
    id: 'triptyque-monts', kind: 'tour', name: 'Mountain Triptych', emoji: '⛰️', author: 'Gruvetto',
    desc: 'Three days for climbers: the Wall, an uphill time trial and a finale at Cloud Pass.',
    stages: [stage('The Wall', 'race', 'mur', 4), stage('Uphill time trial', 'tt', 'nuages', 1), stage('The Clouds', 'race', 'nuages', 2)],
    jerseys: [jersey('gc'), jersey('mountain'), jersey('combative')], tags: ['tour', 'mountain'],
  }),
  lib({
    id: 'coupe-kart', kind: 'tour', name: 'Arcade Cup', emoji: '🍌', author: 'Gruvetto',
    desc: 'Four rounds, three modes: Grand Prix, time trial, elimination and a final. Points classification like a championship.',
    stages: [stage('Round 1 · Grand Prix', 'race', 'crit', 3), stage('Round 2 · Time trial', 'tt', 'collines', 1), stage('Round 3 · Elimination', 'elim', 'mur', 1), stage('Final', 'race', 'collines', 3)],
    jerseys: [jersey('gc', { name: 'Leader’s jersey' }), jersey('combative'), jersey('wins')], scoring: { gc: 'points', bonuses: false }, tags: ['tour', 'items'],
  }),
  lib({
    id: 'gp-sprinteurs', kind: 'event', name: 'Sprinters’ Grand Prix', emoji: '⚡', author: 'Gruvetto',
    desc: 'Five laps of the criterium, four intermediate sprints: every pass over the line earns points.',
    stages: [stage('Grand Prix', 'race', 'crit', 5, { sprints: [0.2, 0.4, 0.6, 0.8] })], jerseys: [jersey('points')], tags: ['sprint'],
  }),
];

const CAL_TEMPLATES: { name: string; emoji: string; desc: string; st: StageDef; cats: CatId[] }[] = [
  { name: 'Criterium sprint', emoji: '⚡', desc: 'Fast criterium, three intermediate sprints.', st: stage('Criterium', 'race', 'crit', 4, { sprints: [0.25, 0.5, 0.75] }), cats: ['A', 'B', 'C', 'D'] },
  { name: 'Cloud Pass climb', emoji: '⛰️', desc: 'Two passes over the summit of Cloud Pass.', st: stage('Cloud Pass', 'race', 'nuages', 2), cats: ['A', 'B', 'C', 'D'] },
  { name: 'Evening elimination', emoji: '❌', desc: 'The last rider of each lap is eliminated.', st: stage('Elimination', 'elim', 'mur', 1), cats: ['B', 'C', 'D', 'E'] },
  { name: 'Hills time trial', emoji: '⏱️', desc: 'Time trial: no items, no drafting.', st: stage('Time trial', 'tt', 'collines', 2), cats: ['A', 'B', 'C', 'D'] },
  { name: 'Rookie Grand Prix', emoji: '🌱', desc: 'A short race with items for new riders, two laps.', st: stage('Grand Prix', 'race', 'crit', 2), cats: ['E'] },
  { name: 'Hills Grand Prix', emoji: '🏆', desc: 'Classic race with items, three laps.', st: stage('Grand Prix', 'race', 'collines', 3), cats: ['A', 'B', 'C', 'D', 'E'] },
];
const SLOTS: [number, number][] = [[12, 30], [18, 0], [19, 30], [21, 0]];

/** Calendar of scheduled events, deterministic: every day, four starts rotating over the templates. */
export function calendar(from = Date.now(), days = 7): EventDef[] {
  const out: EventDef[] = [], d0 = new Date(from); d0.setHours(0, 0, 0, 0);
  for (let d = 0; d < days; d++) {
    const day = new Date(d0); day.setDate(d0.getDate() + d);
    const dayN = Math.floor(day.getTime() / 86400000);
    SLOTS.forEach(([h, m], k) => {
      const at = new Date(day); at.setHours(h, m, 0, 0);
      if (at.getTime() < from - 3600000) return;
      const t = CAL_TEMPLATES[(dayN * SLOTS.length + k) % CAL_TEMPLATES.length], ymd = `${day.getFullYear()}${String(day.getMonth() + 1).padStart(2, '0')}${String(day.getDate()).padStart(2, '0')}`;
      out.push(normalizeEvent({ id: `cal-${ymd}-${h}${String(m).padStart(2, '0')}`, kind: 'event', name: t.name, emoji: t.emoji, desc: t.desc, startAt: at.getTime(), lateJoin: true, categories: t.cats, stages: [t.st], jerseys: t.st.sprints.length ? ['points'] : t.st.mode === 'race' && t.st.track === 'nuages' ? ['mountain'] : [], seed: hash(ymd + k), author: 'Gruvetto', tags: ['calendar'] }, { now: from }).def);
    });
  }
  return out;
}

/** Finds a built-in event (library or calendar, from its dated identifier). */
export function findBuiltinEvent(id: string): EventDef | null {
  const l = EVENT_LIBRARY.find(e => e.id === id);
  if (l) return l;
  const m = /^cal-(\d{4})(\d{2})(\d{2})-/.exec(id);
  if (!m) return null;
  const day = new Date(+m[1], +m[2] - 1, +m[3]).getTime();
  return calendar(day, 1).find(e => e.id === id) || null;
}

export const modeName = (m: StageMode) => MODES[m]?.name || m;
